import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { projects } from "../config/projectsContent";
import YoutubeEmbed from "./YoutubeEmbed";

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const closeModal = () => {
    setIsOpen(false);
    setSelectedProject(null);
  };

  const openModal = (project) => {
    setSelectedProject(project);
    setIsOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            <span className="border-b-4 border-blue-500 pb-2">Projects</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore my latest work and side projects
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
            >
              {/* Image Container */}
              <div className="relative h-56 overflow-hidden bg-gray-100">
                <img
                  src={project.imgSrc}
                  alt={project.title}
                  className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
                />
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow">
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 mb-2">
                    {project.title}
                  </h3>
                  {/* Fixed height description container */}
                  <div className="h-24 overflow-hidden relative mb-2">
                    <p className="text-gray-600 text-sm">
                      {project.details.length > 150
                        ? `${project.details.substring(0, 150)}...`
                        : project.details}
                    </p>
                    {project.details.length > 150 && (
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent"></div>
                    )}
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.techStack.slice(0, 4).map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full
                        transform hover:-translate-y-1 transition-all duration-200 hover:bg-blue-50 hover:text-blue-600"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.techStack.length > 4 && (
                    <span className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full">
                      +{project.techStack.length - 4} more
                    </span>
                  )}
                </div>

                {/* Actions - Pushed to bottom with margin-top auto */}
                <div className="flex items-center space-x-4 mt-auto">
                  {project.siteURL && (
                    <a
                      href={project.siteURL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg
                        hover:bg-blue-600 transition-colors duration-300"
                    >
                      <span>Visit Site</span>
                      <svg
                        className="w-4 h-4 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  )}
                  <button
                    onClick={() => openModal(project)}
                    className="inline-flex items-center px-4 py-2 border-2 border-gray-300 text-gray-600
                      rounded-lg hover:bg-gray-50 hover:border-blue-500 hover:text-blue-500
                      transition-all duration-300"
                  >
                    <span>Learn More</span>
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog
          onClose={closeModal}
          className="fixed inset-0 z-50 overflow-y-auto"
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                {selectedProject && (
                  <>
                    <div className="relative h-64 -mx-6 -mt-6 mb-6 bg-gray-100">
                      <img
                        src={selectedProject.imgSrc}
                        alt={selectedProject.title}
                        className="w-full h-full object-contain p-4"
                      />
                      <button
                        onClick={closeModal}
                        className="absolute top-4 right-4 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors duration-200"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    <Dialog.Title
                      as="h3"
                      className="text-2xl font-bold text-gray-900 mb-4"
                    >
                      {selectedProject.title}
                    </Dialog.Title>

                    <div className="mb-6">
                      <p className="text-gray-600 whitespace-pre-line">
                        {selectedProject.details}
                      </p>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">
                        Technologies Used
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProject.techStack.map((tech, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {selectedProject.youtubeEmbed && (
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">
                          Demo Video
                        </h4>
                        <div className="relative pt-[56.25%]">
                          <YoutubeEmbed
                            embedId={selectedProject.youtubeEmbed}
                          />
                        </div>
                      </div>
                    )}

                    {selectedProject.siteURL && (
                      <div className="mt-8">
                        <a
                          href={selectedProject.siteURL}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg
                            hover:bg-blue-600 transition-colors duration-300"
                        >
                          Visit Project
                          <svg
                            className="w-5 h-5 ml-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
